import Stack from '@mui/material/Stack'
import { useContext } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { Switch, TagsField, TextField } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { PREDEFINED_AD_UNIT_SIZES } from 'features/globalSetup/features/viewport/constants'
import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { useAbility } from 'providers/casl'
import { Schema } from '../schema'

export const RefreshRedirectSection = (): React.JSX.Element => {
  const { t } = useTranslation('features/adUnit', {
    keyPrefix: 'form.refresher.refreshRedirect',
  })
  const { adUnit } = useContext(AddEditAdUnitContext)
  const { control } = useFormContext<Schema>()
  const ability = useAbility()
  const sendRefreshRequestToExternalAdUnitEnabled = useWatch({
    control,
    name: 'refreshRedirect.sendRefreshRequestToExternalAdUnitEnabled',
  })

  const cannotUpdateAdUnit = ability.cannot('update', 'AdUnit')

  if (adUnit === undefined) {
    throw Error('Ad unit should be accessible')
  }

  return (
    <Paper data-cy='refresh-redirect-section'>
      <SectionHeader
        subtitle={t('subtitle')}
        title={t('title')}
      />

      <GapBox />

      <Switch
        control={control}
        disabled={cannotUpdateAdUnit}
        label={t('sendRefreshRequestToExternalAdUnitEnabled.label')}
        labelPlacement='start'
        name='refreshRedirect.sendRefreshRequestToExternalAdUnitEnabled'
        tooltip={{ content: t('sendRefreshRequestToExternalAdUnitEnabled.tooltip') }}
      />

      <GapBox />

      {sendRefreshRequestToExternalAdUnitEnabled && (
        <Stack direction='column'>
          <TextField
            control={control}
            disabled={cannotUpdateAdUnit}
            label={t('columns.targetAdUnitPath.label')}
            name='refreshRedirect.targetAdUnitPath'
            placeholder={t('columns.targetAdUnitPath.placeholder')}
            tooltip={{ content: t('columns.targetAdUnitPath.tooltip') }}
            width='lg'
          />

          <GapBox />

          <TagsField
            control={control}
            disabled={cannotUpdateAdUnit}
            label={t('columns.additionalSizes.label')}
            name='refreshRedirect.additionalSizes'
            options={PREDEFINED_AD_UNIT_SIZES}
            tooltip={{ content: t('columns.additionalSizes.tooltip') }}
            width='lg'
          />
        </Stack>
      )}
    </Paper>
  )
}
